import { type NavigateOptions } from 'react-router'

import { cleanJoin } from 'utils/strings'

import type { Addresses } from 'components/services/dependency-form-utils'
import type { PolicyEvaluation } from 'types/api/policies/policy'
import type { ReliabilityReport } from 'types/api/policies/reliability-report'
import type { ReliabilityTestRun } from 'types/api/reliability-tests/runs'

/**
 * Get PolicyEvaluations for only dependencies (as opposed to RM tests)
 */
export function getDependencyPolicyEvaluations(
  reliabilityReport: ReliabilityReport | undefined
) {
  if (!reliabilityReport?.reliability) return []

  return (
    Object.values(reliabilityReport.reliability)
      // Grab all the policy evaluations
      .flatMap((summary) => summary?.policyStates)
      // only the ones impacting dependencies
      .filter((evaluation) => Boolean(evaluation.dependencyId))
  )
}

/**
 * Make a set of test definitions by collecting PolicyEvaluations per dependencyId
 */
export function getPolicyEvaluationsByDependencyId(
  reliabilityReport: ReliabilityReport | undefined
) {
  if (!reliabilityReport) return

  return getDependencyPolicyEvaluations(reliabilityReport).reduce<{
    [dependencyId: string]: PolicyEvaluation[]
  }>((evaluationsByDependency, evaluation) => {
    const lastEvaluationDependencyId = evaluation.dependencyId

    if (!lastEvaluationDependencyId) return evaluationsByDependency

    const evaluations =
      evaluationsByDependency[lastEvaluationDependencyId] ?? []
    evaluations.push(evaluation)
    evaluations.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

    evaluationsByDependency[lastEvaluationDependencyId] = evaluations

    return evaluationsByDependency
  }, {})
}

export function getHistoryRunUrl({
  dependencyId,
  failureFlagName,
  serviceId,
  testId
}: {
  dependencyId?: string | null
  failureFlagName?: string | null
  serviceId: string
  testId: string
}) {
  const baseRoute = `/services/${serviceId}/history/${testId}`

  if (dependencyId) return `${baseRoute}/${dependencyId}`
  if (failureFlagName) return `${baseRoute}/failure-flag/${failureFlagName}`

  return `${baseRoute}`
}

/**
 * A service test is run on A) the service itself or B) a dependency of that service.
 * If `dependencyId` is included, the url will include it (B). Otherwise, it goes to (A).
 */
export function getStatusRunUrl({
  dependencyId,
  failureFlagName,
  runNumber,
  runPageSection,
  serviceId,
  testId
}: {
  dependencyId?: string | null
  failureFlagName?: string | null
  runNumber?: number
  runPageSection?: 'diagnosis'
  serviceId: string
  testId: string
}) {
  const baseSegment = `/services/${serviceId}/status/${testId}`
  const typeSegment =
    dependencyId ??
    (failureFlagName ? `failure-flag/${failureFlagName}` : undefined)
  const runSegment = runNumber ? `run/${runNumber}` : undefined
  const hashSuffix = runPageSection && runNumber ? `#${runPageSection}` : ''

  return `${cleanJoin(
    [baseSegment, typeSegment, runSegment],
    '/'
  )}${hashSuffix}`
}

export function getIsCurrentlyRunning(
  isServiceRunningATest?: boolean,
  runningReliabilityTest?: ReliabilityTestRun | null,
  reliabilityTestId?: string,
  dependencyId?: string | null
) {
  if (!isServiceRunningATest) return false

  return (
    runningReliabilityTest?.guid === reliabilityTestId &&
    runningReliabilityTest?.dependencyId === dependencyId
  )
}

export function getDependencyUrl(
  serviceId: string,
  dependencyId: string
): [string, NavigateOptions] {
  return [
    `/services/${serviceId}/dependencies#${dependencyId}`,
    {
      preventScrollReset: true
    }
  ]
}

export function getDependencyEditUrl(
  serviceId: string,
  dependencyId: string
): [string, NavigateOptions] {
  return [
    `/services/${serviceId}/dependencies/edit#${dependencyId}`,
    {
      preventScrollReset: true
    }
  ]
}

export function getDependencyIdFromLocationForEdit(
  locationPathname: string,
  locationHash: string | undefined
) {
  return locationPathname.endsWith('dependencies/edit')
    ? locationHash?.slice(1)
    : undefined
}

export const MULTIPLE_IDENTIFIERS_DEFAULT_TEXT =
  'Multiple identifiers are rolled into this dependency. Tests will target all listed identifiers.'

export function hasMultipleIdentifiers(addresses?: Addresses | string[]) {
  return addresses && addresses.length > 1
}
